/**
 * A persona's attributes are its values for the {placeholders} in the
 * assistant's prompt. They are saved as a key -> value object and edited as
 * rows. The rules mirror the backend (libs/common/evalPersonaAttributes), which
 * enforces them again on save.
 */

export type PersonaAttributeRowT = { key: string; value: string };

export type PersonaAttributesValidationT = {
  isValid: boolean;
  // One entry per row, undefined when the row is fine.
  rowErrors: Array<string | undefined>;
  // A problem with the list as a whole (too many attributes).
  error: string | undefined;
};

export const MAX_PERSONA_ATTRIBUTES = 50;
export const MAX_PERSONA_ATTRIBUTE_VALUE_LENGTH = 2000;

// The pattern the backend matches {placeholders} with — a key outside it could
// never fill one.
const ATTRIBUTE_KEY_PATTERN = /^\w+$/;

export function toAttributeRows(attributes): Array<PersonaAttributeRowT> {
  return Object.entries(attributes ?? {}).map(([key, value]) => ({
    key,
    value: String(value ?? ""),
  }));
}

// A row added and then left empty is ignored rather than reported.
function isBlankRow(row: PersonaAttributeRowT) {
  return !row.key.trim() && !row.value;
}

export function toAttributesObject(
  rows: Array<PersonaAttributeRowT>,
): Record<string, string> {
  const attributes: Record<string, string> = {};
  for (const row of rows) {
    if (isBlankRow(row)) continue;
    attributes[row.key.trim()] = row.value;
  }
  return attributes;
}

export function validateAttributeRows(
  rows: Array<PersonaAttributeRowT>,
): PersonaAttributesValidationT {
  const seenKeys = new Set<string>();
  let filledRows = 0;

  const rowErrors = rows.map((row) => {
    if (isBlankRow(row)) return undefined;
    filledRows += 1;

    const key = row.key.trim();

    if (!key) return "Key is required";

    if (!ATTRIBUTE_KEY_PATTERN.test(key) || key === "__proto__") {
      return "Key can only contain letters, numbers and underscores";
    }

    if (seenKeys.has(key)) return "This key is already used above";
    seenKeys.add(key);

    if (row.value.length > MAX_PERSONA_ATTRIBUTE_VALUE_LENGTH) {
      return `Value can be at most ${MAX_PERSONA_ATTRIBUTE_VALUE_LENGTH} characters`;
    }

    return undefined;
  });

  const error =
    filledRows > MAX_PERSONA_ATTRIBUTES
      ? `A persona can have at most ${MAX_PERSONA_ATTRIBUTES} attributes`
      : undefined;

  return {
    isValid: !error && rowErrors.every((rowError) => !rowError),
    rowErrors,
    error,
  };
}
